import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { DropdownMenu } from '@features/shared/components/DropdownMenu'
import { IconButton } from '@features/shared/components/IconButton'
import { EyeOffIcon, HeartIcon } from '@features/shared/icons/icons'
import {
  DEFAULT_FAVORITE_FILTER,
  DEFAULT_HIDDEN_FILTER,
  FAVORITE_FILTER_OPTIONS,
  HIDDEN_FILTER_OPTIONS,
  nextFavoriteFilter,
  nextHiddenFilter,
  type FavoriteFilter,
  type HiddenFilter,
} from '../logic/menuFilters'
import { MENU_ICON_SLOT_CLASS } from './menuIconSlots'

const MORE_OPTIONS_HINT = '(Right click for more options)'
// Above the sidebar (z 10000), like its rows' menus.

/**
 * A menu header's filter: a click switches between its two everyday choices;
 * a right click (or Shift+F10, or the menu key) opens all three, and a click
 * while they're open only closes them, as a click anywhere else does. Tinted
 * in its tone and filled while the menu isn't showing its default.
 */
function FilterToggle<T extends string>({
  value,
  defaultValue,
  options,
  next,
  toneClass,
  renderIcon,
  onChange,
}: {
  value: T
  defaultValue: T
  options: readonly { value: T; label: string }[]
  next: (value: T) => T
  toneClass: string
  renderIcon: (active: boolean) => ReactNode
  onChange: (value: T) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const active = value !== defaultValue
  const clickValue = next(value)
  const clickLabel = options.find((option) => option.value === clickValue)?.label ?? ''
  const openMenu = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault()
    setMenuOpen(true)
  }

  return (
    <DropdownMenu
      open={menuOpen}
      onOpenChange={setMenuOpen}
      triggerMode="manual"
      align="right"
      className="min-w-48"
      sections={[
        {
          items: options.map((option) => ({
            type: 'option' as const,
            id: option.value,
            label: option.label,
            selected: option.value === value,
            onSelect: () => onChange(option.value),
          })),
        },
      ]}
      trigger={
        <IconButton
          label={`${clickLabel} ${MORE_OPTIONS_HINT}`}
          aria-pressed={active}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => (menuOpen ? setMenuOpen(false) : onChange(clickValue))}
          onContextMenu={openMenu}
          onKeyDown={(event) => {
            if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
              openMenu(event)
            }
          }}
          className={`${MENU_ICON_SLOT_CLASS} transition-colors ${
            active
              ? `${toneClass} text-[color:var(--tone-icon)] hover:text-[color:var(--tone-text)]`
              : 'text-[color:var(--sidebar-submenu-text-faint)] hover:text-[color:var(--sidebar-submenu-text-subtle)]'
          }`}
        >
          {renderIcon(active)}
        </IconButton>
      }
    />
  )
}

/** The header's eye: shows the menu's hidden rows (dimmed), or only them. */
export function HiddenFilterToggle({
  value,
  onChange,
}: {
  value: HiddenFilter
  onChange: (value: HiddenFilter) => void
}) {
  return (
    <FilterToggle
      value={value}
      defaultValue={DEFAULT_HIDDEN_FILTER}
      options={HIDDEN_FILTER_OPTIONS}
      next={nextHiddenFilter}
      toneClass="tone-purple"
      renderIcon={(active) => <EyeOffIcon size={15} filled={active} aria-hidden="true" />}
      onChange={onChange}
    />
  )
}

/** The header's heart: shows only the menu's favorites, or only the rest. */
export function FavoriteFilterToggle({
  value,
  onChange,
}: {
  value: FavoriteFilter
  onChange: (value: FavoriteFilter) => void
}) {
  return (
    <FilterToggle
      value={value}
      defaultValue={DEFAULT_FAVORITE_FILTER}
      options={FAVORITE_FILTER_OPTIONS}
      next={nextFavoriteFilter}
      toneClass="tone-rose"
      renderIcon={(active) => <HeartIcon size={15} filled={active} aria-hidden="true" />}
      onChange={onChange}
    />
  )
}
